import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { COMPANY, HERO_VIDEO, HERO_VIDEO_ALT, ASSETS, scrollToId } from "@/lib/site";

const LINES = ["FREIGHT THAT", "MOVES ON", "YOUR SCHEDULE."];

const wrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.25 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const mediaScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden pb-14"
    >
      <motion.div style={{ y: mediaY, scale: mediaScale }} className="absolute inset-0 z-0">
        <video
          data-testid="hero-video"
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={ASSETS.heroTruck}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
          <source src={HERO_VIDEO_ALT} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-ink/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="relative z-10 mx-auto mt-24 flex w-full max-w-[1600px] items-center justify-between px-5 md:px-10"
      >
        <span className="text-xs uppercase tracking-[0.3em] text-[#d4d4d8]">
          EST. {COMPANY.since} — Houston, TX
        </span>
        <span className="hidden text-xs uppercase tracking-[0.3em] text-[#d4d4d8] md:block">
          DOT-Ready · Specialized Freight
        </span>
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto w-full max-w-[1600px] px-5 md:px-10">
        <motion.h1
          variants={wrap}
          initial="hidden"
          animate="show"
          className="font-display text-[13vw] font-black uppercase leading-[0.86] tracking-tighter md:text-[9.5vw]"
        >
          {LINES.map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={line} className="block">
                {i === 1 ? (
                  <>
                    MOVES <span className="text-brand">ON</span>
                  </>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-md text-base leading-relaxed text-[#e4e4e7]">
            A Houston-based owner-operated carrier hauling specialized and long-distance freight across
            the lower 48. On-time, tracked, and handled like it's our own.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#contact");
              }}
              data-testid="hero-quote-btn"
              className="group bg-brand px-8 py-4 text-sm font-semibold uppercase tracking-wide text-ink transition-colors hover:bg-white"
            >
              Request a Quote
            </a>
            <a
              href="#careers"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#careers");
              }}
              data-testid="hero-careers-btn"
              className="border border-white/40 px-8 py-4 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:border-brand hover:text-brand"
            >
              Drive With Us
            </a>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="relative z-10 mx-auto mt-12 flex w-full max-w-[1600px] items-center gap-3 px-5 text-xs uppercase tracking-[0.3em] text-[#d4d4d8] md:px-10"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-brand" />
        Scroll to explore
      </motion.div>
    </section>
  );
}
